import React, { useMemo, useState } from "react";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import * as Yup from "yup";
import { toast } from "react-toastify";
import FormDatePicker from "../../components/common/form-date-picker";
import SearchableSelect from "../../components/common/searchable-select";
import RecurringPreview from "../common/recurring-preview";
import HolidayConflictEdit from "../common/recurring-holiday-conflict-edit";
import useHolidays from "../../hooks/use-holidays";
import RequestRecurringService from "../../services/requestRecurringService";

const DAY_LABELS = ["จ", "อ", "พ", "พฤ", "ศ", "ส", "อา"];
const DISABLED_DAYS = [5, 6];
const DAY_NAMES = ["จันทร์", "อังคาร", "พุธ", "พฤหัสบดี", "ศุกร์", "เสาร์", "อาทิตย์"];
const WEEK_ORDER = ["ที่หนึ่ง", "ที่สอง", "ที่สาม", "ที่สี่", "สุดท้าย"];
const FREQ_UNIT_OPTIONS = [
  { value: "week",  label: "สัปดาห์ (Week)" },
  { value: "month", label: "เดือน (Month)" },
];

const FREQ_NUM_OPTIONS = Array.from({ length: 7 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1),
}));

const getMonthlyOptions = (startDate) => {
  if (!startDate) return [];
  const d = new Date(startDate);
  const dayOfMonth = d.getDate();
  const dayName = DAY_NAMES[d.getDay() === 0 ? 6 : d.getDay() - 1];
  const weekNum = Math.ceil(dayOfMonth / 7);
  const daysInMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  const isLastWeek = daysInMonth - dayOfMonth < 7;

  const opts = [{ value: "day_of_month", label: `ในวันที่ ${dayOfMonth}` }];
  if (weekNum <= 4) opts.push({ value: "week_of_month", label: `ในวัน${dayName} ${WEEK_ORDER[weekNum - 1]}` });
  if (isLastWeek)  opts.push({ value: "last_week",     label: `ในวัน${dayName} สุดท้าย` });
  return opts;
};

const formatDateTH = (date) => {
  if (!date) return "";
  const d = new Date(date);
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
};

const toISODate = (date) => {
  if (!date) return "";
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const DAY_CODE = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

const countWeekdayOccurrencesInMonth = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const weekday = date.getDay();
  let count = 0;
  for (let day = 1; day <= daysInMonth; day++) {
    if (new Date(year, month, day).getDay() === weekday) count++;
  }
  return count;
};

const buildPreviewPayload = (values) => {
  const frequencyTypeMap = { week: 2, month: 3 };
  const frequency_type = frequencyTypeMap[values.freq_unit] || 0;

  let recurrence_type = 0;
  if (values.freq_unit === "week") recurrence_type = 20;
  if (values.monthly_type === "day_of_month") recurrence_type = 31;
  if (values.monthly_type === "week_of_month") recurrence_type = 32;
  if (values.monthly_type === "last_week") recurrence_type = 32;

  const recurrence_detail = values.freq_unit === "week"
    ? values.repeat_days?.map((i) => DAY_CODE[i]).join(", ") || ""
    : (() => {
        const d = new Date(values.start_date);
        const dayOfMonth = d.getDate();
        const dayCode = DAY_CODE[d.getDay() === 0 ? 6 : d.getDay() - 1];
        if (values.monthly_type === "day_of_month") return String(dayOfMonth);
        if (values.monthly_type === "week_of_month") {
          const weekNum = Math.ceil(dayOfMonth / 7);
          return `${dayCode}:${weekNum}`;
        }
        if (values.monthly_type === "last_week") {
          const totalOccurrences = countWeekdayOccurrencesInMonth(d);
          return `${dayCode}:${totalOccurrences}`;
        }
        return "";
      })();

  return {
    start_date: toISODate(values.start_date),
    end_date: toISODate(values.end_date),
    frequency_type,
    recurrence_type,
    recurrence_detail,
    every: Number(values.freq_num) || 1,
    replacements_date: "",
    status: 1,
  };
};

const extractPreviewDates = (result) => {
  if (!result || !Array.isArray(result.dates)) return [];
  return result.dates.map((iso) => formatDateTH(iso));
};

const extractUnavailableDates = (result) => {
  if (!result || !Array.isArray(result.unavailable_dates)) return [];
  return result.unavailable_dates.map((iso) => formatDateTH(iso));
};

const generateDates = ({ start_date, until_date, freq_num, freq_unit, repeat_days, monthly_type }) => {
  const dates = [];
  const current = new Date(start_date);
  const end = new Date(until_date);
  const every = Number(freq_num) || 1;

  if (freq_unit === "week") {
    // ── จุดอ้างอิง: วันจันทร์ของสัปดาห์ที่ start_date อยู่ (ใช้นับว่าอยู่สัปดาห์ที่เท่าไร) ──
    const base = new Date(start_date);
    base.setHours(0, 0, 0, 0);
    const baseDayIndex = base.getDay() === 0 ? 6 : base.getDay() - 1;
    base.setDate(base.getDate() - baseDayIndex);

    while (current <= end) {
      const dayIndex = current.getDay() === 0 ? 6 : current.getDay() - 1;
      if (repeat_days.includes(dayIndex)) {
        const today = new Date(current.getFullYear(), current.getMonth(), current.getDate());
        const weekIndex = Math.floor((today - base) / (7 * 86400000));
        if (weekIndex % every === 0) {
          dates.push(formatDateTH(new Date(current)));
        }
      }
      current.setDate(current.getDate() + 1);
    }
  } else if (freq_unit === "month") {
    const originDate = new Date(start_date);
    const dayOfMonth = originDate.getDate();
    const targetDayIndex = originDate.getDay() === 0 ? 6 : originDate.getDay() - 1;
    const weekNum = Math.ceil(dayOfMonth / 7);

    while (current <= end) {
      const daysInMonth = new Date(current.getFullYear(), current.getMonth() + 1, 0).getDate();
      const currentDayIndex = current.getDay() === 0 ? 6 : current.getDay() - 1;
      const currentWeekNum = Math.ceil(current.getDate() / 7);
      const isLastWeek = daysInMonth - current.getDate() < 7;

      const match =
        (monthly_type === "day_of_month" && current.getDate() === dayOfMonth) ||
        (monthly_type === "week_of_month" && currentDayIndex === targetDayIndex && currentWeekNum === weekNum) ||
        (monthly_type === "last_week" && currentDayIndex === targetDayIndex && isLastWeek);

      if (match) {
        dates.push(formatDateTH(new Date(current)));
        current.setDate(1);
        current.setMonth(current.getMonth() + every);
        current.setDate(current.getDate() - 1);
      } else {
        current.setDate(current.getDate() + 1);
      }
    }
  }

  return dates;
};

const showBackConfirmAlert = async () => {
  const result = await Swal.fire({
    title: "ยังไม่ได้บันทึกข้อมูล",
    html: `<p class="txt">ยังไม่ทำการกดบันทึกข้อมูล ยืนยันที่จะย้อนกลับหรือไม่</p>`,
    showCancelButton: true,
    confirmButtonText: "Confirm",
    cancelButtonText:  "Cancel",
    confirmButtonColor: "#3b82f6",
    customClass: {
      popup: "comfirm-popup",
      confirmButton: "swal-btn-save",
      cancelButton: "swal-popup-btn-cancel",
    },
    buttonsStyling: false,
  });
  return result.isConfirmed;
};

// ── Main Component ────────────────────────────────────────────────────────────
const RecurringSetting = ({
  onSave,
  onBack,
  onCancel,
  onSaveComplete,
  onDelete,
  initialValues,
  buildMainPayload,
  isSubmitting,
  serviceDate,
}) => {
  const [view, setView] = useState("form");
  const [previewDates, setPreviewDates]   = useState([]);
  const [unavailableDates, setUnavailableDates]   = useState([]);
  const [conflictDates, setConflictDates] = useState([]);
  const [previewing, setPreviewing]       = useState(false); 

  const { isDateAvailable } = useHolidays();

  const hasExistingRecurring = !!(initialValues?.start_date || initialValues?.freq_unit);

  const minStartDate = useMemo(() => {
    if (!serviceDate) return null;
    const d = new Date(serviceDate);
    if (isNaN(d.getTime())) return null;
    d.setHours(0, 0, 0, 0);
    return d;
  }, [serviceDate]);

  const isStartDateSelectable = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);

    if (minStartDate && d < minStartDate) return false;

    const day = d.getDay();
    if (day === 0 || day === 6) return false;

    if (!isDateAvailable(date)) return false;

    return true;
  };

  const isEndDateSelectable = (date) => {
    const day = new Date(date).getDay();
    if (day === 0 || day === 6) return false;
    return isDateAvailable(date);
  };

  const F = useFormik({
    enableReinitialize: true,
    initialValues: {
      start_date:   initialValues?.start_date  || null,
      end_date:     initialValues?.end_date     || null,
      freq_num:     initialValues?.freq_num     || "",
      freq_unit:    initialValues?.freq_unit    || "",
      repeat_days:  initialValues?.repeat_days  || [],
      monthly_type: initialValues?.monthly_type || "",
    },
    validationSchema: Yup.object({
      start_date: Yup.date()
        .nullable()
        .required("กรุณากรอกข้อมูล/Please fill out")
        .test(
          "min-service-date",
          "วันที่เริ่มต้นต้องไม่ก่อนวันที่ให้บริการ (Service Date)",
          function (value) {
            if (!value || !minStartDate) return true;
            const v = new Date(value);
            v.setHours(0, 0, 0, 0);
            return v >= minStartDate;
          }
        )
        .test(
          "no-weekend",
          "วันที่เริ่มต้นต้องไม่เป็นวันเสาร์-อาทิตย์",
          function (value) {
            if (!value) return true;
            const day = new Date(value).getDay();
            return day !== 0 && day !== 6;
          }
        ),
      end_date:   Yup.date().nullable().required("กรุณากรอกข้อมูล/Please fill out"),
      freq_num:   Yup.number().required("กรุณากรอกข้อมูล/Please fill out"),
      freq_unit:  Yup.string().required("กรุณากรอกข้อมูล/Please fill out"),
    }),
    onSubmit: async (values) => {
      setPreviewing(true);
      try {
        const payload = buildPreviewPayload(values);

        // ═══════════════════════════════════════════════════════════
        // 🔌 API CALL: POST /requests/recurring/preview
        // ขอรายการวันทั้งหมด + วันที่ชน weekend/holiday จาก backend
        // ═══════════════════════════════════════════════════════════
        const res = await RequestRecurringService.PreviewRequestRecurring(payload);

        if (res?.data?.is_success) {
          const result = res.data.result;
          setPreviewDates(extractPreviewDates(result)); // dates ทั้งหมด (ยังไม่ตัด)
          setUnavailableDates(extractUnavailableDates(result)); // วันที่ชน weekend/holiday
          setView("preview");
        } else {
          toast.error("ไม่สามารถคำนวณรายการคำขอได้");
        }
      } catch (err) {
        // console.error("preview error:", err); 
        toast.error("เกิดข้อผิดพลาดในการคำนวณรายการคำขอ");
      } finally {
        setPreviewing(false);
      }
    },
  });

  const handleCancel = () => {
    F.resetForm();
    setPreviewDates([]);
    setUnavailableDates([]);
    setConflictDates([]);
    setView("form");
    onCancel?.();
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: "ลบการตั้งค่าทำซ้ำ",
      html: `<p class="txt">ยืนยันที่จะลบการตั้งค่าทำซ้ำนี้หรือไม่</p>`,
      showCancelButton: true,
      confirmButtonText: "Confirm",
      cancelButtonText:  "Cancel",
      customClass: {
        popup: "comfirm-popup",
        confirmButton: "swal-btn-save",
        cancelButton: "swal-popup-btn-cancel",
      },
      buttonsStyling: false,
    });
    if (result.isConfirmed) {
      await Swal.fire({
        html: `
          <div style="text-align:center; padding: 8px 0">
            <div style="width:48px;height:48px;background:#fee2e2;border-radius:50%;display:flex;align-items:center;justify-content:center;margin:0 auto 12px">
              <i class="mdi mdi-close-circle" style="color:#ef4444;font-size:28px"></i>
            </div>
            <div style="font-weight:700;font-size:16px;margin-bottom:6px; color:#1E293B;">ทำการลบคำขอทั้งหมดแล้ว</div>
            <div style="color:#6b7280;font-size:13px">สามารถตั้งค่าการทำซ้ำได้อีกครั้ง ที่ปุ่ม ‘ตั้งค่าทำซ้ำ’</div>
          </div>
        `,
        showConfirmButton: false,
        timer: 2500,
        customClass: { popup: "comfirm-popup" },
        buttonsStyling: false,
      });
      onDelete?.();
    }
  };

  const showErr = (name) =>
    F.touched[name] && F.errors[name]
      ? <div className="rs-invalid-feedback">{F.errors[name]}</div>
      : null;

  const toggleDay = (dayIndex) => {
    const next = F.values.repeat_days.includes(dayIndex)
      ? F.values.repeat_days.filter((d) => d !== dayIndex)
      : [...F.values.repeat_days, dayIndex];
    F.setFieldValue("repeat_days", next);
  };

  const monthlyOptions = useMemo(() => getMonthlyOptions(F.values.start_date), [F.values.start_date]);

  const previewText = useMemo(() => {
    const { start_date, end_date, freq_num, freq_unit, repeat_days, monthly_type } = F.values;
    const sd = formatDateTH(start_date);
    const ed = formatDateTH(end_date);
    if (!sd || !ed || !freq_unit) return "—";

    if (freq_unit === "week") {
      const sorted = [...repeat_days].sort();
      const dayStr = sorted.length > 0
        ? sorted.map((i) => DAY_NAMES[i]).join("และ")
        : "(ยังไม่ได้เลือกวัน)";
      return `เกิดทุก${freq_num > 1 ? ` ${freq_num} สัปดาห์ ` : ""}วัน${dayStr} ตั้งแต่ ${sd} ถึง ${ed}`;
    }
    if (freq_unit === "month") {
      const label = monthlyOptions.find((o) => o.value === monthly_type)?.label || "";
      return `เกิดทุก ${freq_num} เดือน ${label} ตั้งแต่ ${sd} ถึง ${ed}`;
    }
    return "—";
  }, [F.values, monthlyOptions]);

  const handleBack = async () => {
    if (F.dirty) {
      const confirmed = await showBackConfirmAlert();
      if (confirmed) onBack();
    } else {
      onBack();
    }
  };

  return (
    <div className="page-wrapper">
      {view === "form" && (
        <div>
          <div className="rs-top-bar">
            <div>
              <div className="rs-page-title">ตั้งค่าให้ทำซ้ำ Recurring Setting</div>
              <div className="page-subtitle">กรอกรายละเอียดในคำขอใช้บริการ</div>
            </div>
            {F.dirty && (
              <button type="button" className="rs-cancel-link" onClick={handleCancel}>
                <i className="mdi mdi-rotate-left" /> Cancel recurring setup
              </button>
            )}
          </div>

          <form onSubmit={F.handleSubmit}>
            <div className="rs-form-card">
              <div className="form-row rs-mb">
                <div className="form-group">
                  <label className="form-label">วันที่เริ่มต้น (Start Date) <span className="req">*</span></label>
                  <FormDatePicker
                    value={F.values.start_date}
                    onChange={(date) => {
                      F.setFieldValue("start_date", date);
                      F.setFieldValue("monthly_type", "");
                      F.setFieldValue("end_date", null);
                    }}
                    isInvalid={F.touched.start_date && !!F.errors.start_date}
                    minDate={minStartDate || undefined}
                    filterDate={isStartDateSelectable}
                  />
                  {showErr("start_date")}
                </div>

                <div className="form-group">
                  <label className="form-label">วันที่สิ้นสุด (End Date) <span className="req">*</span></label>
                  <FormDatePicker
                    value={F.values.end_date}
                    onChange={(date) => {
                      F.setFieldValue("end_date", date);
                    }}
                    isInvalid={F.touched.end_date && !!F.errors.end_date}
                    minDate={F.values.start_date}
                    filterDate={isEndDateSelectable}
                  />
                  {showErr("end_date")}
                </div>
              </div>

              <div className="rs-recurring-note rs-mb">
                <i className="mdi mdi-alert-circle-outline" />
                การตั้งค่าทำซ้ำแต่ละครั้งจะใช้ระยะเวลาตามช่วงวันนั้น
              </div>

              <label className="form-label">เกิดซ้ำทุก (Repeat every)</label>
              <div className="repeat">
                <SearchableSelect
                  id="freq_num"
                  name="freq_num"
                  value={F.values.freq_num}
                  onChange={(e) => F.setFieldValue("freq_num", e.target.value)}
                  onBlur={() => F.setFieldTouched("freq_num", true)}
                  options={FREQ_NUM_OPTIONS}
                  placeholder="เลือกจำนวน"
                />

                <SearchableSelect
                  id="freq_unit"
                  name="freq_unit"
                  value={F.values.freq_unit}
                  onChange={(e) => {
                    F.setFieldValue("freq_unit", e.target.value);
                    F.setFieldValue("repeat_days", []);
                    F.setFieldValue("monthly_type", "");
                  }}
                  onBlur={() => F.setFieldTouched("freq_unit", true)}
                  options={FREQ_UNIT_OPTIONS}
                  placeholder="เลือกความถี่"
                />
              </div>

              {F.values.freq_unit === "week" && (
                <div className="form-group rs-mb">
                  <label className="form-label">เกิดซ้ำในวัน</label>
                  <div className="rs-days-row">
                    {DAY_LABELS.map((label, i) => (
                      <button
                        key={i}
                        type="button"
                        className={[
                          "rs-day-btn",
                          F.values.repeat_days.includes(i) ? "active"   : "",
                          DISABLED_DAYS.includes(i)        ? "disabled" : "",
                        ].join(" ").trim()}
                        onClick={() => toggleDay(i)}
                        disabled={DISABLED_DAYS.includes(i)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {F.values.freq_unit === "month" && (
                <div className="form-group rs-mb">
                  <label className="form-label">เกิดซ้ำในวันที่</label>
                  <div className="rs-radio-group">
                    {monthlyOptions.map((opt) => (
                      <label key={opt.value} className="rs-radio-label">
                        <input
                          type="radio"
                          name="monthly_type"
                          value={opt.value}
                          checked={F.values.monthly_type === opt.value}
                          onChange={() => F.setFieldValue("monthly_type", opt.value)}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {previewText !== "—" && (
                <div className="form-group">
                  <label className="form-label">ผลลัพธ์การตั้งค่า (Preview)</label>
                  <div className="rs-preview-box">{previewText}</div>
                </div>
              )}
            </div>

            <div className="form-footer d-flex justify-content-between">
              <div>
                {hasExistingRecurring && (
                  <button
                    type="button"
                    className="btn btn-cancel"
                    onClick={handleDelete}
                  > Delete Recurring Request
                  </button>
                )}
              </div>
              <div className="rp-footer-actions">
                <button
                  type="button"
                  className="btn btn-secondary btn-back"
                  onClick={handleBack}
                  disabled={previewing}
                >
                  <i className="ri-arrow-left-line" /> Back
                </button>
                <button type="submit" className="btn btn-primary" disabled={previewing}>
                  {previewing ? "กำลังคำนวณ..." : "Save"}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {view === "preview" && (
        <RecurringPreview
          dates={previewDates}
          unavailableDates={unavailableDates}
          buildMainPayload={null}
          recurringPayload={buildPreviewPayload(F.values)}
          onBack={() => setView("form")}
          onCancel={handleCancel}
          onEditConflict={(dates) => {
            setConflictDates(dates);
            setView("edit-conflict");
          }}
          onSaveSuccess={() => {
            setView("form");
            onSave?.(F.values);
            toast.success("ตั้งค่าสำเร็จแล้ว", {
              toastClassName: "custom-toast",
              bodyClassName:  "custom-toast-body",
              icon: () => <i className="mdi mdi-check-circle-outline" />,
            });
            onSaveComplete?.();
          }}
        />
      )}

      {view === "edit-conflict" && (
        <HolidayConflictEdit
          conflictDates={conflictDates}
          onBack={() => setView("preview")}
          onCancel={handleCancel}
          onSave={async (resolvedMap) => {
            const updated = previewDates
              .map((d) => (d in resolvedMap ? resolvedMap[d] : d))
              .filter(Boolean);
            setPreviewDates(updated);

            await onSave?.({ ...F.values, replacements: resolvedMap });

            toast.success("ตั้งค่าสำเร็จแล้ว", {
              toastClassName: "custom-toast",
              bodyClassName:  "custom-toast-body",
              icon: () => <i className="mdi mdi-check-circle-outline" />,
            });

            onSaveComplete?.();
          }}
        />
      )}
    </div>
  );
};

export default RecurringSetting;